import { debounce } from "lodash";
import { useState, useEffect, useRef, useCallback } from "react";

const useChipsResize = ({
  items,
  containerRef,
  seeMoreRef,
  defaultMaxVisibleChips = 0,
}) => {
  const [maxVisibleChips, setMaxVisibleChips] = useState(
    defaultMaxVisibleChips
  );
  const chipRefs = useRef([]);
  const lastComputedValue = useRef(null);
  const observerDisconnected = useRef(false);
  const isScrolling = useRef(false);

  const calculateChipsPerRow = useCallback(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const seeMoreWidth = seeMoreRef.current?.offsetWidth || 0;
    const containerStyle = window.getComputedStyle(containerRef.current);
    const containerPadding =
      parseInt(containerStyle.paddingLeft, 10) +
      parseInt(containerStyle.paddingRight, 10);

    const availableWidth = containerWidth - containerPadding * 2 - seeMoreWidth;

    // Measure chip widths correctly (including margins)
    const chipWidths = chipRefs.current
      .filter((chip) => chip)
      .map((chip) => {
        const width = chip?.offsetWidth || 100;
        const computedStyle = window.getComputedStyle(chip);
        const margin =
          parseInt(computedStyle.marginLeft, 10) +
          parseInt(computedStyle.marginRight, 10);

        return width + margin * 2;
      });

    if (chipWidths.length === 0) return;

    // Calculate the average width of chips
    const avgChipWidth = Math.max(
      100,
      Math.floor(chipWidths.reduce((a, b) => a + b, 0) / chipWidths.length)
    );

    // Calculate how many chips fit per row based on available width
    const chipsPerRow = Math.floor(availableWidth / avgChipWidth);

    // Use a more conservative value for `maxRows` i.e 2 rows max
    const newMaxVisibleChips = Math.min(items.length, chipsPerRow * 2);

    // Check whether we don't exceed 2 rows
    if (
      newMaxVisibleChips !== lastComputedValue.current &&
      newMaxVisibleChips > chipsPerRow
    ) {
      lastComputedValue.current = newMaxVisibleChips;
      setMaxVisibleChips(newMaxVisibleChips);
    }

    // Disconnect the observer only after we've set the maxVisibleChips to 2 rows
    if (
      !observerDisconnected.current &&
      newMaxVisibleChips === chipsPerRow * 2
    ) {
      observerDisconnected.current = true;
      if (containerRef.current && containerRef.current.disconnect) {
        containerRef.current.disconnect();
      }
    }
  }, [items.length, containerRef, seeMoreRef]);

  useEffect(() => {
    const debouncedResize = debounce(calculateChipsPerRow, 500);
    let resizeObserver = new ResizeObserver(() => {
      if (!isScrolling.current) {
        debouncedResize();
        resizeObserver.disconnect();
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    calculateChipsPerRow();

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateChipsPerRow, containerRef]);

  return { maxVisibleChips, chipRefs };
};

export default useChipsResize;
